import { ROUTE_PATH } from '@shared/enum/common.enum';
import { ICommonBackendFields } from '@shared/model/common.model';

export type Permission = 'read' | 'write' | 'update' | 'delete' | 'full';

export const ReadPermission: Permission = 'read';
export const WritePermission: Permission = 'write';
export const UpdatePermission: Permission = 'update';
export const DeletePermission: Permission = 'delete';
export const FullPermission: Permission = 'full';

export enum PERMISSIONS_TYPES {
    Read = 'read',
    Write = 'write',
    Update = 'update',
    Delete = 'delete',
    Full = 'full',
}

export interface IUserPermissions {
    [moduleId: number]: Permission[];
}

export interface IPermission {
    module_id: number;
    module_name: string;
    permission_types: Permission[];
}

export interface IModule {
    id: number;
    name: string;
}

export interface IModuleFull extends ICommonBackendFields, IModule {}

export enum MODULE {
    HOME = 1,
    PROFILE = 2,
    PRODUCTION_DASHBOARD = 3,
    MEMBER_MANAGEMENT = 4,
    ROLE_MANAGEMENT = 5,
    TEAM_MANAGEMENT = 6,
    KNOWLEDGE_BANK = 7,
    PROGRAM = 8,
    CONFIGURE_PROGRAM = 9,
    FUNDAMENTAL = 10,
    EXPORT_PROGRAM = 11,
    COURSE = 12,
    PROGRAM_CATALOGUE = 13,
    OVERVIEW = 14,
    LESSON = 15,
    CHAPTER = 16,
    SECTION = 17,
    COMMENT = 18,
    ACCEPT_COMMENT = 19,
    REJECT_COMMENT = 20,
    SEARCH_TEAM = 21,
    IMPORT_MEMBERS = 22,
    SWITCH_ROLE = 23,
    FILE = 24,
    DOWNLOAD_FILE = 25,
    SEARCH_FILE = 26,
    RE_ORDERING = 27,
    CHANGE_PASSWORD = 28,
    LANGUAGE = 29,
    COURSE_USER_ASSIGNMENT = 30,
}

// This map holds the relationship between dynamic route paths and static module IDs.
export const RouteModuleMapping: Record<string, number> = {
    [ROUTE_PATH.DASHBOARD]: MODULE.HOME,
    [ROUTE_PATH.CREATE_COURSE]: MODULE.COURSE,
    [ROUTE_PATH.CUSTOMIZE_COURSE]: MODULE.COURSE,
    [ROUTE_PATH.MANUSCRIPT]: MODULE.COURSE,
    [ROUTE_PATH.PRODUCTION]: MODULE.COURSE,
    [ROUTE_PATH.PROGRAM]: MODULE.PROGRAM,
    [ROUTE_PATH.ADD_PROGRAM_CATALOGUE]: MODULE.PROGRAM,
    [`${ROUTE_PATH.EDIT_PROGRAM_CATALOGUE}/:id`]: MODULE.PROGRAM,
    [ROUTE_PATH.FUNDAMENTAL]: MODULE.CONFIGURE_PROGRAM,
    [ROUTE_PATH.KNOWLEDGE_GOALS]: MODULE.CONFIGURE_PROGRAM,
    [ROUTE_PATH.TARGET_AUDIENCE]: MODULE.CONFIGURE_PROGRAM,
    [ROUTE_PATH.PRE_REQUISITES]: MODULE.CONFIGURE_PROGRAM,
    [ROUTE_PATH.TRAINING_OBJECTIVES]: MODULE.CONFIGURE_PROGRAM,
    [ROUTE_PATH.CONTENT_DESCRIPTION]: MODULE.CONFIGURE_PROGRAM,
    [ROUTE_PATH.USE_CASES]: MODULE.CONFIGURE_PROGRAM,
    [ROUTE_PATH.EKC_MATRIX]: MODULE.CONFIGURE_PROGRAM,
    [ROUTE_PATH.COURSE]: MODULE.COURSE,
    [`${ROUTE_PATH.COURSE}/:id/${ROUTE_PATH.LESSON}`]: MODULE.LESSON,
    [`${ROUTE_PATH.COURSE}/:id/${ROUTE_PATH.LESSON}/:lessonId/${ROUTE_PATH.CHAPTER}`]:
        MODULE.CHAPTER,
    [`${ROUTE_PATH.COURSE}/:id/${ROUTE_PATH.LESSON}/:lessonId/${ROUTE_PATH.CHAPTER}/:chapterId/${ROUTE_PATH.EDIT}`]:
        MODULE.CHAPTER,
    [`${ROUTE_PATH.COURSE}/:id/${ROUTE_PATH.LESSON}/:lessonId/${ROUTE_PATH.CHAPTER}/:chapterId/${ROUTE_PATH.SECTION}`]:
        MODULE.SECTION,
    [`${ROUTE_PATH.COURSE}/:id/${ROUTE_PATH.LESSON}/:lessonId/${ROUTE_PATH.CHAPTER}/:chapterId/${ROUTE_PATH.SECTION}/:sectionId/${ROUTE_PATH.EDIT}`]:
        MODULE.SECTION,
    [`${ROUTE_PATH.COURSE}/:id/${ROUTE_PATH.LESSON}/:lessonId/${ROUTE_PATH.CHAPTER}/:chapterId/${ROUTE_PATH.SECTION_TAB}`]:
        MODULE.SECTION,
    [`${ROUTE_PATH.COURSE}/:id/${ROUTE_PATH.LESSON}/:lessonId/${ROUTE_PATH.EDIT}`]:
        MODULE.LESSON,
    [`${ROUTE_PATH.COURSE}/:id/${ROUTE_PATH.EDIT}`]: MODULE.COURSE,
    [`${ROUTE_PATH.COURSE}/:id/${ROUTE_PATH.LESSON}/:id`]: MODULE.LESSON,
    [ROUTE_PATH.OVERVIEW]: MODULE.OVERVIEW,
    [`${ROUTE_PATH.OVERVIEW}/:id`]: MODULE.OVERVIEW,
    [ROUTE_PATH.TEAM_MANAGEMENT]: MODULE.TEAM_MANAGEMENT,
    [ROUTE_PATH.MEMBERS]: MODULE.MEMBER_MANAGEMENT,
    [ROUTE_PATH.ADD_MEMBER]: MODULE.MEMBER_MANAGEMENT,
    [`${ROUTE_PATH.EDIT_MEMBER}/:id`]: MODULE.MEMBER_MANAGEMENT,
    [ROUTE_PATH.ROLE]: MODULE.ROLE_MANAGEMENT,
    [ROUTE_PATH.ADD_ROLE]: MODULE.ROLE_MANAGEMENT,
    [`${ROUTE_PATH.EDIT_ROLE}/:id`]: MODULE.ROLE_MANAGEMENT,
    [ROUTE_PATH.PRODUCTION_DASHBOARD]: MODULE.PRODUCTION_DASHBOARD,
    [`${ROUTE_PATH.PRODUCTION_DASHBOARD}/:id`]: MODULE.PRODUCTION_DASHBOARD,

    [ROUTE_PATH.HOME]: MODULE.HOME,
    [ROUTE_PATH.PROFILE]: MODULE.PROFILE,

    [ROUTE_PATH.KNOWLEDGE_BANK]: MODULE.KNOWLEDGE_BANK,
    [ROUTE_PATH.CONFIGURE_PROGRAM]: MODULE.CONFIGURE_PROGRAM,
    fundamental: MODULE.FUNDAMENTAL,
    'export-program': MODULE.EXPORT_PROGRAM,
};

// Add route names who needs write access
export const RouteWithWriteAccess = [
    ROUTE_PATH.ADD_MEMBER,
    ROUTE_PATH.ADD_ROLE,
    ROUTE_PATH.ADD_PROGRAM_CATALOGUE,
    ROUTE_PATH.COURSE,
    ROUTE_PATH.CONFIGURE_PROGRAM,
    ROUTE_PATH.FUNDAMENTAL,
    ROUTE_PATH.KNOWLEDGE_GOALS,
    ROUTE_PATH.TARGET_AUDIENCE,
    ROUTE_PATH.PRE_REQUISITES,
    ROUTE_PATH.TRAINING_OBJECTIVES,
    ROUTE_PATH.CONTENT_DESCRIPTION,
    ROUTE_PATH.USE_CASES,
    ROUTE_PATH.EKC_MATRIX,
    `${ROUTE_PATH.COURSE}/:id/${ROUTE_PATH.LESSON}`,
    `${ROUTE_PATH.COURSE}/:id/${ROUTE_PATH.LESSON}/:lessonId/${ROUTE_PATH.CHAPTER}`,
    `${ROUTE_PATH.COURSE}/:id/${ROUTE_PATH.LESSON}/:lessonId/${ROUTE_PATH.CHAPTER}/:chapterId/${ROUTE_PATH.SECTION}`,
];

// Add route names who needs update access
export const RouteWithUpdateAccess = [
    ROUTE_PATH.EDIT_MEMBER,
    `${ROUTE_PATH.EDIT_MEMBER}/:id`,
    ROUTE_PATH.EDIT_ROLE,
    `${ROUTE_PATH.EDIT_ROLE}/:id`,
    ROUTE_PATH.EDIT_PROGRAM_CATALOGUE,
    `${ROUTE_PATH.EDIT_PROGRAM_CATALOGUE}/:id`,
    ROUTE_PATH.CONFIGURE_PROGRAM,
    ROUTE_PATH.FUNDAMENTAL,
    ROUTE_PATH.KNOWLEDGE_GOALS,
    ROUTE_PATH.TARGET_AUDIENCE,
    ROUTE_PATH.PRE_REQUISITES,
    ROUTE_PATH.TRAINING_OBJECTIVES,
    ROUTE_PATH.CONTENT_DESCRIPTION,
    ROUTE_PATH.USE_CASES,
    ROUTE_PATH.EKC_MATRIX,
    `${ROUTE_PATH.COURSE}/:id/${ROUTE_PATH.EDIT}`,
    `${ROUTE_PATH.COURSE}/:id/${ROUTE_PATH.LESSON}/:lessonId/${ROUTE_PATH.EDIT}`,
    `${ROUTE_PATH.COURSE}/:id/${ROUTE_PATH.LESSON}/:lessonId/${ROUTE_PATH.CHAPTER}/:chapterId/${ROUTE_PATH.EDIT}`,
    `${ROUTE_PATH.COURSE}/:id/${ROUTE_PATH.LESSON}/:lessonId/${ROUTE_PATH.CHAPTER}/:chapterId/${ROUTE_PATH.SECTION}/:sectionId/${ROUTE_PATH.EDIT}`,
];

// Add route names who needs update access
export const RouteWithReadAccess = [
    `${ROUTE_PATH.COURSE}/:id/${ROUTE_PATH.EDIT}`,
    `${ROUTE_PATH.COURSE}/:id/${ROUTE_PATH.LESSON}/:lessonId/${ROUTE_PATH.EDIT}`,
    `${ROUTE_PATH.COURSE}/:id/${ROUTE_PATH.LESSON}/:lessonId/${ROUTE_PATH.CHAPTER}/:chapterId/${ROUTE_PATH.EDIT}`,
    `${ROUTE_PATH.COURSE}/:id/${ROUTE_PATH.LESSON}/:lessonId/${ROUTE_PATH.CHAPTER}/:chapterId/${ROUTE_PATH.SECTION}/:sectionId/${ROUTE_PATH.EDIT}`,
];
